import { gql, useMutation, useQuery } from '@apollo/client'

export type SalesNotificationType = 'LEAD_ASSIGNED' | 'TASK_REMINDER' | 'TASK_OVERDUE' | 'STALE_LEAD'

/** An in-app sales alert (matches the GraphQL Notification type). */
export interface SalesNotification {
  id: string
  type: SalesNotificationType
  title: string
  body?: string | null
  entityType?: string | null
  entityId?: string | null
  isRead: boolean
  createdAt?: string | null
}

export const MY_SALES_NOTIFICATIONS = gql`
  query MySalesNotifications {
    myNotifications(page: 0, size: 20) {
      content {
        id
        type
        title
        body
        entityType
        entityId
        isRead
        createdAt
      }
    }
    unreadNotificationCount
  }
`

const MARK_NOTIFICATION_READ = gql`
  mutation MarkNotificationRead($notificationId: ID!) {
    markNotificationRead(notificationId: $notificationId) {
      id
      isRead
    }
  }
`

const MARK_ALL_NOTIFICATIONS_READ = gql`
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead
  }
`

/**
 * Deep link for an alert, derived from the entity it points at. Returns null when the alert has no
 * navigable target, so the caller can render it as plain text.
 */
export function salesNotificationRoute(n: SalesNotification): string | null {
  if (!n.entityId) return null
  if (n.entityType === 'CONTACT') return `/sales/contacts/${n.entityId}`
  if (n.entityType === 'TASK') return '/sales/tasks'
  return null
}

/**
 * Sales alerts for the topbar bell: lead assigned, follow-up reminder, overdue task, stale lead.
 *
 * `errorPolicy: 'all'` mirrors the action-request inbox so a user without sales access (the query is
 * role-guarded server-side) degrades to an empty list instead of breaking the whole bell.
 */
export function useSalesNotifications() {
  const { data, refetch, loading } = useQuery(MY_SALES_NOTIFICATIONS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    pollInterval: 60000,
  })
  const [markReadMutation] = useMutation(MARK_NOTIFICATION_READ)
  const [markAllReadMutation] = useMutation(MARK_ALL_NOTIFICATIONS_READ)

  const all: SalesNotification[] = data?.myNotifications?.content ?? []
  const unread = all.filter((n) => !n.isRead)

  const markRead = async (notificationId: string) => {
    await markReadMutation({ variables: { notificationId } })
    await refetch()
  }
  const markAllRead = async () => {
    await markAllReadMutation()
    await refetch()
  }

  return {
    notifications: all,
    unread,
    count: data?.unreadNotificationCount ?? unread.length,
    loading,
    markRead,
    markAllRead,
    refetch,
  }
}
